import React, { useCallback, useState } from 'react';
import { MapPin, Save, RefreshCw, Download, Calculator, Users, Calendar, Plane, Home, Coffee, Tv } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import DestinationSection from './DestinationSection';
import TransportSection from './TransportSection';
import AccommodationSection from './AccommodationSection';
import FoodSection from './FoodSection';
import EntertainmentSection from './EntertainmentSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости поездки
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setExpensesBreakdown,
  russianDestinations,
  transportTypes,
  accommodationTypes,
  transportHubs,
  saveTrip,
  calculateExpensesBreakdown,
  seasons
}) => {
  
  // Состояние для имени сохраняемой поездки
  const [saveTripName, setSaveTripName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Сбросим ошибки для измененного поля
      if (errors[name]) {
        setErrors(prev => {
          const updatedErrors = { ...prev };
          delete updatedErrors[name];
          return updatedErrors;
        });
      }
      
      // Логика автообновления сезона поездки
      if (name === 'destination' || name === 'travelMonth') {
        if (newState.travelingSeason === 'auto') {
          // Автоопределение сезона в зависимости от места и месяца
          const currentMonth = parseInt(newState.travelMonth, 10);
          const destination = russianDestinations.find(d => d.id === newState.destination);
          
          if (destination && destination.season) {
            if (destination.season.peak && destination.season.peak.includes(currentMonth)) {
              newState.autoDetectedSeason = 'peak';
            } else if (destination.season.high && destination.season.high.includes(currentMonth)) {
              newState.autoDetectedSeason = 'high';
            } else if (destination.season.low && destination.season.low.includes(currentMonth)) {
              newState.autoDetectedSeason = 'low';
            } else {
              newState.autoDetectedSeason = 'shoulder';
            }
          }
        }
      }
      
      // Обновляем ориентировочное расстояние при изменении пунктов отправления/назначения
      if (name === 'originCity' || name === 'destination') {
        // Здесь можно добавить расчет примерного расстояния между городами
        // для более точного расчета стоимости транспорта
      }
      
      // Логика изменений для самокатеринга
      if (name === 'selfCatering' && checked) {
        // Если выбрано самостоятельное приготовление еды, уменьшаем затраты на еду
        newState.foodOption = 'budget';
      }
      
      return newState;
    });
  }, [errors, russianDestinations, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      destination, tripDuration, travelers, transportType,
      accommodationType, accommodationRating, foodOption, entertainmentLevel
    } = formData;

    // Валидация пункта назначения
    if (!destination) {
      newErrors.destination = 'Выберите пункт назначения';
    }
    
    // Валидация продолжительности поездки
    const numTripDuration = parseIntOrDefault(tripDuration, 0);
    if (!isPositiveNumber(tripDuration) || numTripDuration < 1) {
      newErrors.tripDuration = 'Продолжительность должна быть больше 0';
    }
    if (numTripDuration > 90) {
      newErrors.tripDuration = 'Максимальная продолжительность — 90 дней';
    }
    
    // Валидация количества путешественников
    const numTravelers = parseIntOrDefault(travelers, 0);
    if (!isPositiveNumber(travelers) || numTravelers < 1) {
      newErrors.travelers = 'Минимум 1 путешественник';
    }
    if (numTravelers > 20) {
      newErrors.travelers = 'Максимум 20 путешественников';
    }
    
    // Валидация типа транспорта
    if (!transportType) {
      newErrors.transportType = 'Выберите тип транспорта';
    }
    
    // Валидация типа проживания
    if (!accommodationType) {
      newErrors.accommodationType = 'Выберите тип проживания';
    }
    
    // Валидация рейтинга проживания для отелей
    if (accommodationType === 'hotel') {
      const rating = parseIntOrDefault(accommodationRating, 0);
      if (rating < 1 || rating > 5) {
        newErrors.accommodationRating = 'Рейтинг должен быть от 1 до 5';
      }
    }
    
    // Валидация опции питания
    if (!foodOption) {
      newErrors.foodOption = 'Выберите опцию питания';
    }
    
    // Валидация уровня развлечений
    if (!entertainmentLevel) {
      newErrors.entertainmentLevel = 'Выберите уровень развлечений';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета стоимости поездки
  const calculateTripCost = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setExpensesBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const destination = russianDestinations.find(d => d.id === formData.destination);
        const transportType = transportTypes.find(t => t.id === formData.transportType);
        const accommodationType = accommodationTypes.find(t => t.id === formData.accommodationType);
        const tripDuration = parseIntOrDefault(formData.tripDuration, 7);
        const numTravelers = parseIntOrDefault(formData.travelers, 1);
        
        // Определение сезона (влияет на коэффициент цены)
        let seasonType = formData.travelingSeason;
        if (seasonType === 'auto') {
          seasonType = formData.autoDetectedSeason || 'shoulder';
        }
        const season = seasons.find(s => s.id === seasonType) || seasons.find(s => s.id === 'shoulder');
        const seasonPriceFactor = season.priceFactor;
        
        // Коэффициент стоимости для конкретного направления
        const destinationCostIndex = destination.costIndex || 1;
        
        // --- Расчет стоимости транспорта ---
        // Базовая стоимость зависит от типа транспорта
        let transportBaseCost = transportType.basePrice || 5000;
        
        // Корректировка стоимости в зависимости от класса обслуживания
        const transportClassFactors = {
          'economy': 1,
          'comfort': 1.5,
          'business': 3
        };
        const transportClassFactor = transportClassFactors[formData.transportClass] || 1;
        
        // Удвоить стоимость, если билет туда-обратно
        const returnTicketFactor = formData.includeReturnTicket ? 2 : 1;
        
        // Итоговая стоимость основного транспорта
        const transportMainCost = transportBaseCost * transportClassFactor * returnTicketFactor * numTravelers * destinationCostIndex;
        
        // Стоимость местного транспорта (если включено)
        const localTransportCost = formData.includeLocalTransport 
          ? 500 * tripDuration * numTravelers * destinationCostIndex 
          : 0;
        
        // Общая стоимость транспорта
        const transportCost = transportMainCost + localTransportCost;
        
        // --- Расчет стоимости проживания ---
        // Базовая стоимость зависит от типа проживания
        let accommodationBaseCost = accommodationType.basePrice || 3000;
        
        // Корректировка стоимости в зависимости от местоположения
        const locationFactors = {
          'city_center': 1.5,
          'midtown': 1.2,
          'outskirts': 0.8
        };
        const locationFactor = locationFactors[formData.accommodationLocation] || 1;
        
        // Корректировка для рейтинга отеля (если это отель)
        let ratingFactor = 1;
        if (formData.accommodationType === 'hotel') {
          const hotelRating = parseIntOrDefault(formData.accommodationRating, 3);
          ratingFactor = 0.7 + (hotelRating * 0.3); // от 1 до 2.2 в зависимости от звездности
        }
        
        // Стоимость завтрака (если включен)
        const breakfastCost = formData.includeBreakfast ? 600 * tripDuration * numTravelers : 0;
        
        // Итоговая ночная стоимость размещения
        const accommodationNightCost = accommodationBaseCost * destinationCostIndex 
                                     * locationFactor * ratingFactor * seasonPriceFactor;
        
        // Общая стоимость проживания (за все ночи)
        const accommodationBaseTotalCost = accommodationNightCost * (tripDuration - 1) * (numTravelers > 1 ? Math.ceil(numTravelers / 2) : 1);
        const accommodationExtraCost = breakfastCost;
        const accommodationCost = accommodationBaseTotalCost + accommodationExtraCost;
        
        // --- Расчет стоимости питания ---
        // Базовые дневные затраты на питание в зависимости от выбранной опции
        const foodBaseCosts = {
          'budget': 800,
          'moderate': 1500,
          'premium': 3000
        };
        const foodBaseDailyCost = foodBaseCosts[formData.foodOption] || 1500;
        
        // Корректировка при самостоятельном приготовлении
        const selfCateringFactor = formData.selfCatering ? 0.7 : 1;
        
        // Стоимость напитков и алкоголя
        const drinksAndAlcoholCost = formData.includeDrinksAndAlcohol 
          ? foodBaseDailyCost * 0.4 * tripDuration * numTravelers * destinationCostIndex
          : 0;
        
        // Общая стоимость питания
        const foodBaseCost = foodBaseDailyCost * tripDuration * numTravelers * selfCateringFactor * destinationCostIndex;
        const foodCost = foodBaseCost + drinksAndAlcoholCost;
        
        // --- Расчет стоимости развлечений ---
        // Базовая стоимость развлечений в зависимости от выбранного уровня
        const entertainmentBaseCosts = {
          'minimal': 500,
          'moderate': 1200,
          'extensive': 2500
        };
        const entertainmentBaseCost = entertainmentBaseCosts[formData.entertainmentLevel] || 1200;
        
        // Стоимость музеев и достопримечательностей
        const museumsAndAttractionsCost = formData.includeMuseumsAndAttractions 
          ? 800 * tripDuration * numTravelers * destinationCostIndex
          : 0;
        
        // Стоимость экскурсий с гидом
        const guidedToursCost = formData.includeGuidedTours 
          ? 2000 * Math.ceil(tripDuration / 3) * numTravelers * destinationCostIndex
          : 0;
        
        // Общая стоимость развлечений
        const otherEntertainmentCost = entertainmentBaseCost * tripDuration * numTravelers * destinationCostIndex;
        const entertainmentCost = otherEntertainmentCost + museumsAndAttractionsCost + guidedToursCost;
        
        // --- Расчет стоимости шоппинга ---
        // Стоимость шоппинга в зависимости от выбранного уровня
        const shoppingBaseCosts = {
          'minimal': 1000,
          'moderate': 3000,
          'extensive': 8000
        };
        const shoppingCost = formData.includeShoppingBudget 
          ? (shoppingBaseCosts[formData.shoppingBudget] || 3000) * numTravelers * destinationCostIndex
          : 0;
        
        // --- Дополнительные расходы ---
        // Стоимость страховки
        const insuranceCost = formData.includeTravelInsurance 
          ? 500 * tripDuration * numTravelers
          : 0;
        
        // Стоимость визы (если применимо)
        const visaCost = formData.includeVisaCost ? 5000 * numTravelers : 0;
        
        // Стоимость международного роуминга
        const roamingCost = formData.includeInternationalRoaming 
          ? 400 * tripDuration * numTravelers
          : 0;
        
        // Резервный фонд
        const emergencyFundPercent = parseFloatOrDefault(formData.emergencyFundPercent, 10);
        
        // Суммируем все основные расходы перед добавлением резервного фонда
        const subtotalBeforeEmergencyFund = transportCost + accommodationCost + foodCost + 
                                          entertainmentCost + shoppingCost + insuranceCost + 
                                          visaCost + roamingCost;
        
        const emergencyFundCost = formData.includeEmergencyFund 
          ? subtotalBeforeEmergencyFund * (emergencyFundPercent / 100)
          : 0;
        
        // Общие дополнительные расходы
        const otherCosts = insuranceCost + visaCost + roamingCost + emergencyFundCost;
        
        // --- Итоговая стоимость поездки ---
        const totalCost = transportCost + accommodationCost + foodCost + 
                         entertainmentCost + shoppingCost + otherCosts;
        
        // Стоимость на одного человека
        const costPerPerson = totalCost / numTravelers;
        
        // Среднедневная стоимость на человека
        const costPerPersonPerDay = costPerPerson / tripDuration;
        
        // Формируем объект с результатами
        const calculatedResults = {
          // Основная информация
          destination: formData.destination,
          tripDuration,
          numTravelers,
          season: seasonType,
          seasonPriceFactor,
          destinationCostIndex,
          
          // Итоговые стоимости
          totalCost,
          costPerPerson,
          costPerPersonPerDay,
          
          // Детализация по категориям
          transportCost,
          transportMainCost,
          localTransportCost,
          
          accommodationCost,
          accommodationBaseCost: accommodationBaseTotalCost,
          accommodationExtraCost,
          
          foodCost,
          foodBaseCost,
          drinksAndAlcoholCost,
          
          entertainmentCost,
          museumsAndAttractionsCost,
          guidedToursCost,
          otherEntertainmentCost,
          
          shoppingCost,
          
          otherCosts,
          insuranceCost,
          visaCost,
          roamingCost,
          emergencyFundCost
        };

        // Добавляем анализ структуры расходов (в процентах)
        calculatedResults.expensesStructure = {
          transport: Math.round((transportCost / totalCost) * 100),
          accommodation: Math.round((accommodationCost / totalCost) * 100),
          food: Math.round((foodCost / totalCost) * 100),
          entertainment: Math.round((entertainmentCost / totalCost) * 100),
          shopping: Math.round((shoppingCost / totalCost) * 100),
          other: Math.round((otherCosts / totalCost) * 100)
        };

        // Устанавливаем результаты
        setResults(calculatedResults);
        
        // Рассчитываем разбивку расходов
        const breakdown = calculateExpensesBreakdown(calculatedResults);
        setExpensesBreakdown(breakdown);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setExpensesBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setExpensesBreakdown, setIsCalculating, russianDestinations, transportTypes, accommodationTypes, seasons, calculateExpensesBreakdown]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      destination: 'moscow',
      originCity: '',
      tripDuration: '7',
      travelers: '2',
      travelMonth: new Date().getMonth(),
      
      transportType: 'plane',
      includeReturnTicket: true,
      transportClass: 'economy',
      
      accommodationType: 'hotel',
      accommodationRating: 3,
      accommodationLocation: 'city_center',
      includeBreakfast: true,
      
      foodOption: 'moderate',
      selfCatering: false,
      includeDrinksAndAlcohol: true,
      
      entertainmentLevel: 'moderate',
      includeGuidedTours: true,
      includeMuseumsAndAttractions: true,
      
      includeShoppingBudget: true,
      shoppingBudget: 'moderate',
      includeTravelInsurance: true,
      includeLocalTransport: true,
      includeInternationalRoaming: false,
      
      includeVisaCost: false,
      travelingSeason: 'auto',
      includeEmergencyFund: true,
      emergencyFundPercent: '10',
    });
    setResults(null);
    setExpensesBreakdown([]);
    setErrors({});
    setSaveTripName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Получаем название места назначения
    const destination = russianDestinations.find(d => d.id === formData.destination);
    const destinationName = destination ? destination.name : 'Unknown';

    // Определяем сезон
    let seasonType = formData.travelingSeason;
    if (seasonType === 'auto') {
      seasonType = formData.autoDetectedSeason || 'shoulder';
    }
    const season = seasons.find(s => s.id === seasonType);
    const seasonName = season ? season.name : 'Межсезонье';

    // Форматирование суммы
    const formatCurrency = (amount) => {
      return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g)
    };

    // Создаем заголовки CSV
    const headers = [
      'Категория', 
      'Стоимость (₽)', 
      'На человека (₽)', 
      'На человека в день (₽)', 
      'Процент от общей стоимости'
    ];

    // Создаем строки данных
    const rows = [
      [
        'Транспорт', 
        formatCurrency(results.transportCost), 
        formatCurrency(results.transportCost / results.numTravelers), 
        formatCurrency(results.transportCost / results.numTravelers / results.tripDuration), 
        results.expensesStructure.transport + '%'
      ],
      [
        'Проживание', 
        formatCurrency(results.accommodationCost), 
        formatCurrency(results.accommodationCost / results.numTravelers), 
        formatCurrency(results.accommodationCost / results.numTravelers / results.tripDuration), 
        results.expensesStructure.accommodation + '%'
      ],
      [
        'Питание', 
        formatCurrency(results.foodCost), 
        formatCurrency(results.foodCost / results.numTravelers), 
        formatCurrency(results.foodCost / results.numTravelers / results.tripDuration), 
        results.expensesStructure.food + '%'
      ],
      [
        'Развлечения', 
        formatCurrency(results.entertainmentCost), 
        formatCurrency(results.entertainmentCost / results.numTravelers), 
        formatCurrency(results.entertainmentCost / results.numTravelers / results.tripDuration), 
        results.expensesStructure.entertainment + '%'
      ],
      [
        'Шоппинг', 
        formatCurrency(results.shoppingCost), 
        formatCurrency(results.shoppingCost / results.numTravelers), 
        formatCurrency(results.shoppingCost / results.numTravelers / results.tripDuration), 
        results.expensesStructure.shopping + '%'
      ],
      [
        'Другие расходы', 
        formatCurrency(results.otherCosts), 
        formatCurrency(results.otherCosts / results.numTravelers), 
        formatCurrency(results.otherCosts / results.numTravelers / results.tripDuration), 
        results.expensesStructure.other + '%'
      ],
      [
        'ИТОГО', 
        formatCurrency(results.totalCost), 
        formatCurrency(results.costPerPerson), 
        formatCurrency(results.costPerPersonPerDay), 
        '100%'
      ]
    ];

    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Место назначения:', destinationName],
      ['Продолжительность:', results.tripDuration + ' дней'],
      ['Количество путешественников:', results.numTravelers],
      ['Сезон:', seasonName],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      ['']  // Пустая строка для разделения
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `поездка_в_${destinationName}_${results.tripDuration}_дней.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения поездки
  const handleSaveTrip = () => {
    if (showSaveDialog) {
      const savedResult = saveTrip(saveTripName || null);
      if (savedResult) {
        alert(`Поездка "${savedResult.name}" успешно сохранена!`);
      }
      setShowSaveDialog(false);
      setSaveTripName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTripCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <MapPin className="w-6 h-6 text-purple-500" />
          Параметры поездки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция направления и основных параметров */}
        <DestinationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianDestinations={russianDestinations}
          seasons={seasons}
        />

        {/* Секция транспорта */}
        <TransportSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          transportTypes={transportTypes}
          transportHubs={transportHubs}
        />

        {/* Секция проживания */}
        <AccommodationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          accommodationTypes={accommodationTypes}
        />

        {/* Секция питания */}
        <FoodSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Секция развлечений */}
        <EntertainmentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Секция дополнительных опций */}
        <AdditionalOptionsSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Диалог сохранения поездки */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveTripName}
                onChange={(e) => setSaveTripName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveTrip}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveTrip}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить поездку
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;